// This code is from:
// <https://www.npmjs.com/package/subtitle>
// <https://github.com/gsantiago/subtitle.js>

// Type definitions for subtitle 2.0
// Project: https://github.com/gsantiago/subtitle.js#readme
// Definitions by: Low Jeng Lam <https://github.com/jenglamlow>
// Definitions: https://github.com/DefinitelyTyped/DefinitelyTyped

/* tslint:disable */
/* eslint-disable */

export interface SubTitleType {
  start: number | string;
  end: number | string;
  text: string;
  setting?: string;
}

/**
 * Parse a SRT or WebVTT string.
 * @param {String} srtOrVtt
 * @return {Array} subtitles
 */
export function parse(srtOrVtt: string): SubTitleType[] {
  if (!srtOrVtt) return [];

  const source = srtOrVtt
    .trim()
    .concat("\n")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^WEBVTT.*\n(?:.*: .*\n)*\n/, "")
    .split("\n");

  return source.reduce(
    (captions: any, row: any, index: number) => {
      let caption = captions[captions.length - 1];

      if (!caption.index) {
        if (/^\d+$/.test(row)) {
          caption.index = parseInt(row, 10);
          return captions;
        }
      }

      if (!caption.hasOwnProperty("start")) {
        const timestamp = parseTimestamps(row);

        if (timestamp) {
          if (timestamp.start) {
            caption.start = timestamp.start;
          }
          if (timestamp.end) {
            caption.end = timestamp.end;
          }
          if (timestamp.settings) {
            caption.settings = timestamp.settings;
          }
        } else if (captions.length > 1) {
          captions[captions.length - 2].text += "\n" + row;
        }
        return captions;
      }

      if (row === "") {
        delete caption.index;
        if (index !== source.length - 1) {
          captions.push({});
        }
      } else {
        caption.text = caption.text ? caption.text + "\n" + row : row;
      }

      return captions;
    },
    [{}]
  );
}

export function stringify(captions: ReadonlyArray<SubTitleType>): string {
  return (
    captions
      .map((caption, index) => {
        return (
          (index > 0 ? "\n" : "") +
          [
            index + 1,
            `${toSrtTime(<any>caption.start)} --> ${toSrtTime(<any>caption.end)}`,
            caption.text,
          ].join("\n")
        );
      })
      .join("\n") + "\n"
  );
}

export function stringifyVtt(captions: ReadonlyArray<SubTitleType>): string {
  return (
    "WEBVTT\n\n" +
    captions
      .map((caption, index) => {
        return (
          (index > 0 ? "\n" : "") +
          [
            index + 1,
            `${toVttTime(<any>caption.start)} --> ${toVttTime(<any>caption.end)}${
              (<any>caption).settings ? " " + (<any>caption).settings : ""
            }`,
            caption.text,
          ].join("\n")
        );
      })
      .join("\n") +
    "\n"
  );
}

export function resync(captions: ReadonlyArray<SubTitleType>, time: number): SubTitleType[] {
  return captions.map((caption) => {
    const start = toMS(<any>caption.start) + time;
    const end = toMS(<any>caption.end) + time;

    return {
      ...caption,
      start: start,
      end: end,
    };
  });
}

/**
 * Return the given SRT timestamp as milleseconds.
 * @param {string|number} timestamp
 * @returns {number} milliseconds
 */
export function toMS(timestamp: string): number {
  if (!isNaN(<any>timestamp)) {
    return <any>timestamp;
  }

  const match = timestamp.match(/^(?:(\d{2,}):)?(\d{2}):(\d{2})[,.](\d{3})$/);

  if (!match) {
    throw new Error('Invalid SRT or VTT time format: "' + timestamp + '"');
  }

  const hours = match[1] ? parseInt(match[1], 10) * 3600000 : 0;
  const minutes = parseInt(match[2], 10) * 60000;
  const seconds = parseInt(match[3], 10) * 1000;
  const milliseconds = parseInt(match[4], 10);

  return hours + minutes + seconds + milliseconds;
}

/**
 * Return the given milliseconds as SRT timestamp.
 * @param timestamp
 * @returns {string}
 */
export function toSrtTime(timestamp: number): string {
  if (isNaN(timestamp)) {
    return <any>timestamp;
  }

  const date = new Date(0, 0, 0, 0, 0, 0, timestamp);

  const hours = zeroFill(2, date.getHours());
  const minutes = zeroFill(2, date.getMinutes());
  const seconds = zeroFill(2, date.getSeconds());
  const ms = timestamp - (hours * 3600000 + minutes * 60000 + seconds * 1000);

  return `${hours}:${minutes}:${seconds},${zeroFill(3, ms)}`;
}

/**
 * Return the given milliseconds as WebVTT timestamp.
 * @param timestamp
 * @returns {string}
 */

export function toVttTime(timestamp: number): string {
  if (isNaN(timestamp)) {
    return <any>timestamp;
  }

  const date = new Date(0, 0, 0, 0, 0, 0, timestamp);

  const hours = zeroFill(2, date.getHours());
  const minutes = zeroFill(2, date.getMinutes());
  const seconds = zeroFill(2, date.getSeconds());
  const ms = timestamp - (hours * 3600000 + minutes * 60000 + seconds * 1000);

  return `${hours}:${minutes}:${seconds}.${zeroFill(3, ms)}`;
}

// Helper functions

/**
 * parseTimestamps
 * @param value
 * @returns {{start: Number, end: Number}}
 */
function parseTimestamps(value: any): any {
  const match =
    /^((?:\d{2,}:)?\d{2}:\d{2}[,.]\d{3}) --> ((?:\d{2,}:)?\d{2}:\d{2}[,.]\d{3})(?: (.*))?$/.exec(
      value
    );
  if (match) {
    const cue: any = {
      start: toMS(match[1]),
      end: toMS(match[2]),
    };
    if (match[3]) {
      cue.settings = match[3];
    }
    return cue;
  }
}

/**
 * Given a number, return a zero-filled string.
 * From http://stackoverflow.com/questions/1267283/
 * @param  {number} width
 * @param  {number} number
 * @return {string}
 */
function zeroFill(width: number, number: number, pad?: any): any {
  if (number === undefined) {
    return function (number: any, pad: any) {
      return zeroFill(width, number, pad);
    };
  }
  if (pad === undefined) pad = "0";
  width -= number.toString().length;
  if (width > 0) return new Array(width + (/\./.test(<any>number) ? 2 : 1)).join(pad) + number;
  return number + "";
}
